import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, MenuItem, Pagination, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdAddCircle, MdClose, MdContentCopy, MdDelete, MdEdit, MdHistory, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';
const LIMIT = 20;

const ACTION_STYLE = {
  create: { bg: '#dcfce7', fg: '#166534' },
  update: { bg: '#fef3c7', fg: '#92400e' },
  delete: { bg: '#fee2e2', fg: '#991b1b' },
};
const actionStyle = (a) => ACTION_STYLE[String(a || '').toLowerCase()] || { bg: '#e0e7ff', fg: '#3730a3' };

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const userName = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'User' : 'System');
const dt = (d) => new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
const browser = (ua) => {
  if (!ua) return 'Unknown';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return ua.slice(0, 24);
};
const label = (k) => k.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase());
const show = (v) => (v !== null && typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v));

const ActionChip = ({ action }) => {
  const s = actionStyle(action);
  return <Chip size="small" label={action} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 11, textTransform: 'uppercase', height: 22, background: s.bg, color: s.fg }} />;
};

const Kpi = ({ title, value, icon: Icon, from, to }) => (
  <Box sx={{ flex: 1, minWidth: 170, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={22} /></Box>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
    </Box>
  </Box>
);

const EMPTY = { action: '', entity: '', userId: '', from: '', to: '' };

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ today: 0, creates: 0, updates: 0, deletes: 0 });
  const [facets, setFacets] = useState({ actions: [], entities: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filters, setFilters] = useState(EMPTY);
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, ...filters };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('logs/activity', params, authToken(), false);
      if (mine !== seq.current) return;
      setLogs(res.logs || []);
      setMeta({ total: res.meta?.total || 0, totalPages: res.meta?.totalPages || 1 });
      if (res.stats) setStats(res.stats);
      if (res.facets) setFacets(res.facets);
    } catch {
      if (mine === seq.current) setError('Failed to fetch activity logs. Please try again.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const dirty = !!search || Object.values(filters).some(Boolean);
  const setRange = (days) => {
    const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setFilters((f) => ({ ...f, from: iso(start), to: iso(end) }));
    setPage(1);
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(selected?.details || {}, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard not available */ }
  };

  const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
  const details = selected?.details && typeof selected.details === 'object' ? Object.entries(selected.details) : [];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Activity log</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Who changed what, and when.</Typography>
        </Box>
        <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="Activity today" value={stats.today} icon={MdHistory} from="#6366f1" to="#4338ca" />
        <Kpi title="Created today" value={stats.creates} icon={MdAddCircle} from="#22c55e" to="#15803d" />
        <Kpi title="Updated today" value={stats.updates} icon={MdEdit} from="#f59e0b" to="#b45309" />
        <Kpi title="Deleted today" value={stats.deletes} icon={MdDelete} from="#ef4444" to="#b91c1c" />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search action, module or IP…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Action" value={filters.action} onChange={(e) => setF('action', e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All actions</MenuItem>
          {facets.actions.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Module" value={filters.entity} onChange={(e) => setF('entity', e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All modules</MenuItem>
          {facets.entities.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="User" value={filters.userId} onChange={(e) => setF('userId', e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">All users</MenuItem>
          {facets.users.map((u) => <MenuItem key={u._id} value={u._id}>{userName(u)}</MenuItem>)}
        </TextField>
        <TextField size="small" type="date" label="From" value={filters.from} onChange={(e) => setF('from', e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={filters.to} onChange={(e) => setF('to', e.target.value)} InputLabelProps={{ shrink: true }} />
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          {[['Today', 0], ['7 days', 7], ['30 days', 30]].map(([t, d]) => (
            <Chip key={t} label={t} size="small" clickable onClick={() => setRange(d)} sx={{ fontFamily: FONT, fontWeight: 700 }} />
          ))}
        </Box>
        {dirty && <Button onClick={() => { setSearch(''); setFilters(EMPTY); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>When</TableCell>
                <TableCell sx={head}>User</TableCell>
                <TableCell sx={head}>Action</TableCell>
                <TableCell sx={head}>Module</TableCell>
                <TableCell sx={head}>Source</TableCell>
                <TableCell sx={head} align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && logs.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No activity matches these filters.</TableCell></TableRow>
              )}
              {!loading && logs.map((log) => (
                <TableRow key={log._id} hover>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 13 }}>
                    <b>{new Date(log.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</b>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{new Date(log.createdAt).toLocaleTimeString('en-IN')}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 13 }}>
                    <b>{userName(log.userId)}</b>
                    {log.userId?.email && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{log.userId.email}</Typography>}
                  </TableCell>
                  <TableCell><ActionChip action={log.action} /></TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>{log.entity}</TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 13 }}>
                    {log.ipAddress || '—'}
                    <Tooltip title={log.userAgent || ''}><Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{browser(log.userAgent)}</Typography></Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View details"><IconButton size="small" onClick={() => setSelected(log)} sx={{ color: '#4f46e5', background: '#4f46e514', borderRadius: '8px' }}><MdVisibility size={17} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} entr{meta.total === 1 ? 'y' : 'ies'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" disabled={loading} />
        </Box>
      </Box>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {selected && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 19 }}>{selected.action} · {selected.entity}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.85 }}>{dt(selected.createdAt)}</Typography>
              </Box>
              <IconButton onClick={() => setSelected(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1.6fr' } }}>
                <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #0ea5e9', borderRadius: '14px', p: 2.25, alignSelf: 'start' }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>Summary</Typography>
                  {[
                    ['User', userName(selected.userId)],
                    ['Email', selected.userId?.email],
                    ['Action', selected.action],
                    ['Module', selected.entity],
                    ['Record ID', selected.entityId],
                    ['IP address', selected.ipAddress],
                    ['Browser', browser(selected.userAgent)],
                  ].map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px dashed #e2e8f0' }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, textAlign: 'right', wordBreak: 'break-all' }}>{v || '—'}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #f59e0b', borderRadius: '14px', p: 2.25 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5 }}>What changed</Typography>
                    <Button size="small" startIcon={<MdContentCopy size={14} />} onClick={copyJson} sx={{ textTransform: 'none', fontWeight: 700 }}>{copied ? 'Copied' : 'Copy JSON'}</Button>
                  </Box>
                  {details.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', py: 3, textAlign: 'center' }}>No details were recorded.</Typography>}
                  <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                    {details.map(([k, v]) => (
                      <Box key={k} sx={{ py: 1, borderBottom: '1px dashed #e2e8f0' }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', fontWeight: 700 }}>{label(k)}</Typography>
                        <Typography component="pre" sx={{ m: 0, fontFamily: v !== null && typeof v === 'object' ? 'monospace' : FONT, fontSize: 13, color: '#0f172a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{show(v)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setSelected(null)} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, '&:hover': { background: 'rgb(255 190 0)' } }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ActivityLog;
